import React, { useState, useEffect, useCallback, useRef } from 'react'
import CustomSelect from './CustomSelect';
import message from "components/CustomMessage";
import { parseSelectOptions as globalParseSelectOptions } from 'utils/formUtils';
import _, { uniqBy, isEmpty, debounce } from 'lodash';

export default function SelectWithPaginatedData({showDefaultError = true, disableScrollPagination = false, minSearchLength = undefined, showSearch = true, allowClear = true, ...props}) {
    const pageAfter = useRef();
    const [option, setOption] = useState(props.options || []);
    const isInputInFocus = useRef(false);
    const {children, ...restProps} = props;
    const [page, setPage] = useState(props.startPage || 0);
    const [loading,setLoading] = useState(false);
    const [maxSizeReached,setmaxSizeReached] = useState(false);
    const size = props.pageSize || 20;
    const [searchValue,setSearchValue] = useState(null);
    const [filterOptions,setFilterOptions] = useState(null);
    const selectedOptionsRef = useRef([]);

    const {apiParams,api,listField,getFilterOptions, detechAPIParamChange = false} = props;
    const listFieldName = listField?.name;
    const apiParamsDependency = detechAPIParamChange ? JSON.stringify(apiParams) : apiParams;
    const resetOptionsPropDependency = detechAPIParamChange ? JSON.stringify(props.resetOptionsProp) : props.resetOptionsProp;
    
    useEffect(()=>{
        if(getFilterOptions && !isEmpty(listField)){
            const { name } = listField;
            const _options = getFilterOptions(name,option);
            setFilterOptions(_options)
        }
    },[getFilterOptions,listField,option])

    useEffect(() => {
        setSearchValue(null);
        setPage(props.startPage || 0);
        pageAfter.current = undefined;
    }, [apiParamsDependency, props.startPage]);

    // Option to reset the list when resetOptionsProp changes
    useEffect(() => {
        setOption(options => _.isEmpty(options) ? options : []);
    }, [resetOptionsPropDependency]);

    useEffect(() => {
        if (!_.isFunction(api)) {
            setOption([]);
            return;
        }
        if(!isEmpty(searchValue) && minSearchLength && searchValue?.length < minSearchLength){
            return;
        }
        let params = { size, page, ...apiParams, pageAfter: pageAfter.current, listFieldName };

        if(disableScrollPagination){
            params = { ...apiParams };
        }

        if (searchValue) {
            params.name = searchValue;
        }
       
       setLoading(true);
       const  { promise, cancel } = api(params);  
       promise.then((response) => {
       let projects = response.projects;
       const content = response.content;

       if (!_.isUndefined(content)) {
        projects = content;
        }

       if (Array.isArray(response)) {
        projects = response;
       }
    
        setOption(option => uniqBy([...option,...(props.parseSelectOptions || globalParseSelectOptions)(projects)], 'value'));
        setmaxSizeReached(isEmpty(projects) && isEmpty(content));     // Check whether maximum size is reached from API.
        if (!_.isEmpty(response.pageAfter)) {
            pageAfter.current = response.pageAfter;
        } else if (projects && projects.length && projects[0]?.otherProperties?.after) {
            pageAfter.current = projects[0].otherProperties.after
        }else{
            pageAfter.current = undefined;
        }
        
        }).catch(() => {
            if(showDefaultError){
                message.error(`Problem fetching ${restProps.name || 'options'}`);
            }
        }).finally(()=>{
           setLoading(false);
        });
    return cancel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchValue, size, apiParamsDependency, api, restProps.name, props.parseSelectOptions, listFieldName, resetOptionsPropDependency, showDefaultError, minSearchLength, disableScrollPagination])  

    const onSearch = debounce((input) => {
        if (isInputInFocus.current) {
            pageAfter.current = undefined;
            if (input === "") {
                setSearchValue(null);
            }
            else {
                setSearchValue(input.trim());
            }
            setPage(props.startPage || 0);
            setOption([]);
        }
    }, 800)

    const onBlur = useCallback(() => {
        isInputInFocus.current = false;
        pageAfter.current = undefined;
        // Reset pagination to start page if page is not equal to props.startPage
        // Otherwise, reset to 0 if props.startPage is undefined
        if (props.startPage ? page !== props.startPage : page !== 0) {
            setOption(uniqBy(selectedOptionsRef.current, 'value')
        );
        }
        setSearchValue(null);
        setPage(props.startPage || 0);
    }, [page, props.startPage])

    const onFocus = useCallback(() => {
        isInputInFocus.current = true;
    }, []);

    const infiniteScroll = useCallback((e) => {
        console.log(e.target.scrollHeight, e.target.scrollTop, e.target.clientHeight)
        const scrollPosition = e.target.scrollHeight - e.target.scrollTop;
        const nearBottom =  scrollPosition > e.target.clientHeight - 1 && scrollPosition < e.target.clientHeight + 1;
        if (nearBottom && !loading) { 
          setPage(page + 1 );
        }
    },[page,loading])

    const onClear = useCallback(() => {
        setSearchValue(null);
        pageAfter.current = undefined;
    },[])



    const onChange = (value,field, selectedOption) => {
        restProps.onChange(value,field,selectedOption);
        selectedOptionsRef.current = Array.isArray(selectedOption) ? selectedOption : [selectedOption];
        setSearchValue(null);
    }

        return (
            <CustomSelect
                {..._.omit(restProps, 'api')}
                // mode="version"
                allowClear={allowClear}
                style={{ width: '100%' }}
                options={isEmpty(filterOptions) ? option : filterOptions }
                onSearch={onSearch}
                onBlur={onBlur}
                onFocus={onFocus}
                showSearch={showSearch}
                filterOption
                onPopupScroll={!maxSizeReached && !disableScrollPagination ? infiniteScroll : () => {}}
                loading={loading}
                onChange={onChange}
                onClear={onClear}
                showValueOnload={props.showValueOnload === undefined ? true : props.showValueOnload}
            />
        )
    }
